import React from "react";
import cn from "classnames";
import parse from "html-react-parser";

import { useTranslation } from "@litres-libs/i18n";
import { isBiblioDomain, getPrice, getHrefForReading } from "@litres/utils";
import { useAppSelector } from "@litres-core/store";
import { Button } from "@litres/ui-kit/button";
import { SmartLink } from "@litres-modules/smart-link";
import {
  formatPrice,
  getAuthors,
  highlightSearchQuery,
  trackerSpotlightClickButton,
  trackerSpotlightClickItem,
} from "../utils";
import { getBookImageURL } from "../../../utils/helpers";
import { ArtType, translatedArtTypes } from "../constants";
import { Image } from "../../Image/Image";
import noCoverIcon from "../../../../static/icons/noCoverIcon.svg";

import styles from "./SellableItem.module.scss";

import type { SellableItemProps } from "../types";

export const SellableItem = (props: SellableItemProps): JSX.Element => {
  const {
    externalRef,
    id,
    url,
    title,
    persons,
    mem,
    price,
    currency,
    promoPrice,
    free,
    availableByPromo,
    freebie,
    libraryServiceType,
    owned,
    percent,
    coverRatio,
    searchQuery,
    setLoading,
    saveSearchQuery,
    eventData,
    isDesktop = true,
    item,
  } = props;

  const { t, i18n } = useTranslation();
  const user = useAppSelector(state => state.foundationUser);
  const isAnon = user.id === 0;
  const clientHost = useAppSelector(
    state => state.browser.headers["client-host"]
  );
  const lang = useAppSelector(state => state.face.language || "ru");

  const isBookOnSale = Boolean(Number(price) - Number(promoPrice));
  const isHidden = true;
  const isFreeBook =
    (Number(price) === 0 || !!free || !!freebie || availableByPromo) && !owned;

  const convertOldPriceToInt = (price: number): number => {
    return Number(
      `${price.toString().slice(0, price.toString().search(/\./g))}`
    );
  };

  const getPriceWithSymbolWrapper = (price: number) => {
    const priceString = getPrice(price, currency ?? undefined, i18n.language);
    const symbol = (priceString?.match(/[^0-9.,\s]+/g) || [])[0];
    if (symbol && priceString) {
      const symbolWithWrapper = (
        <span className={styles.litresRuble}>{symbol}</span>
      );
      const priceWithoutSymbol = priceString.replace(symbol, "");
      return priceString?.startsWith(symbol) ? (
        <>
          {symbolWithWrapper}
          {priceWithoutSymbol}
        </>
      ) : (
        <>
          {priceWithoutSymbol}
          {symbolWithWrapper}
        </>
      );
    }
    return priceString;
  };

  const getArtActionFromType = mem === ArtType.Text ? "Читать" : "Слушать";

  const handleSearchItemClick = (): void => {
    setLoading(true);
    if (!isAnon && searchQuery) {
      saveSearchQuery(searchQuery);
    }
  };

  const isAvailableInLib = libraryServiceType === "instant";
  const isNotAvailableInLib = libraryServiceType === "delayed";

  const isPodcastParent = mem === ArtType.Podcast;

  const getReadNowHref = (): string => {
    return getHrefForReading({
      art: item,
      user,
      isLibrary: isBiblioDomain(clientHost || ""),
      lang,
    });
  };

  const getArtLink = (): string => {
    const isAvailableAsFree = isFreeBook && !owned && !isPodcastParent;
    const isAvailableAsOwned = !!owned && !isPodcastParent;

    if (!isDesktop && (isAvailableAsOwned || isAvailableAsFree)) {
      return getReadNowHref();
    }

    return url || "#";
  };

  return (
    <li
      className={cn(styles.wrapper, { [styles.mobileWrapper]: !isDesktop })}
      tabIndex={0}
      key={url}
      ref={externalRef}
      onClick={(e: React.MouseEvent): void => {
        handleSearchItemClick();
        trackerSpotlightClickItem(eventData, e, getArtLink());
      }}
    >
      <SmartLink href={getArtLink()} className={styles.item}>
        <div className={styles.coverContainer}>
          <span className={styles.coverWrapper}>
            <Image
              src={getBookImageURL(Number(id))}
              alt={title}
              style={{
                width: 30,
                height: Math.round(30 / Number(coverRatio)),
              }}
              fallbackSrc={noCoverIcon}
            />
          </span>
        </div>
        <div className={styles.details}>
          <div>
            <span className={styles.authors}>
              {persons
                ? getAuthors(persons, mem, t)
                : t(translatedArtTypes[mem])}
            </span>
          </div>
          <div>
            <span
              className={cn(styles.title, {
                [styles.ownedArt]: owned,
              })}
            >
              {searchQuery &&
                title &&
                parse(highlightSearchQuery(searchQuery, title))}
            </span>
          </div>
        </div>

        {isDesktop && (
          <div className="buttonWrapper">
            {isAvailableInLib && !owned && !isPodcastParent && url && (
              <Button
                mode="secondary"
                size="small"
                onClick={(
                  e: React.MouseEvent<HTMLElement, MouseEvent>
                ): void => {
                  e.stopPropagation();
                  e.preventDefault();
                  trackerSpotlightClickButton(eventData, false);
                  window.location.href = `${url}#take_youself`;
                }}
                text={t("Взять себе")}
              />
            )}
            {isFreeBook && !owned && !isPodcastParent && (
              <Button
                mode="secondary"
                size="small"
                onClick={(
                  e: React.MouseEvent<HTMLElement, MouseEvent>
                ): void => {
                  e.stopPropagation();
                  e.preventDefault();
                  trackerSpotlightClickButton(eventData, false);
                  window.location.href = getReadNowHref();
                }}
                text={t(getArtActionFromType)}
              />
            )}
            {!!owned && !percent && !isPodcastParent && (
              <Button
                mode="secondary"
                size="small"
                onClick={(
                  e: React.MouseEvent<HTMLElement, MouseEvent>
                ): void => {
                  e.stopPropagation();
                  e.preventDefault();
                  trackerSpotlightClickButton(eventData, false);
                  window.location.href = getReadNowHref();
                }}
                text={t(getArtActionFromType)}
              />
            )}
            {!!owned && !!percent && !isPodcastParent && (
              <Button
                mode="secondary"
                size="small"
                className={cn(styles.button, styles.longButton)}
                onClick={(
                  e: React.MouseEvent<HTMLElement, MouseEvent>
                ): void => {
                  e.stopPropagation();
                  e.preventDefault();
                  trackerSpotlightClickButton(eventData, false);
                  window.location.href = getReadNowHref();
                }}
                text={
                  mem === ArtType.Text
                    ? t("Продолжить чтение с {percent}%", { percent: percent })
                    : t("Продолжить с {percent}%", { percent: percent })
                }
              />
            )}
            {isNotAvailableInLib &&
              !owned &&
              !isPodcastParent &&
              !isHidden &&
              url && (
                <Button
                  size="small"
                  mode="secondary"
                  onClick={(
                    e: React.MouseEvent<HTMLElement, MouseEvent>
                  ): void => {
                    e.stopPropagation();
                    e.preventDefault();
                    trackerSpotlightClickButton(eventData, false);
                    window.location.href = `${url}#take_youself`;
                  }}
                  text={t("Запросить")}
                />
              )}
            {isBookOnSale &&
              !owned &&
              !isAvailableInLib &&
              !isFreeBook &&
              !isNotAvailableInLib &&
              !isPodcastParent &&
              !isHidden &&
              url &&
              price &&
              promoPrice && (
                <Button
                  mode="secondary"
                  size="medium"
                  onClick={(
                    e: React.MouseEvent<HTMLElement, MouseEvent>
                  ): void => {
                    e.stopPropagation();
                    e.preventDefault();
                    trackerSpotlightClickButton(eventData, true);
                    window.location.href = isAnon
                      ? `${url}#buy_now_noreg`
                      : `${url}#buy_now_reg`;
                  }}
                  secondaryText={
                    <div className={styles.buttonOldPrice}>
                      {getPriceWithSymbolWrapper(
                        convertOldPriceToInt(price || 100)
                      )}
                    </div>
                  }
                  text={
                    <div className={styles.buttonNewPrice}>
                      {getPriceWithSymbolWrapper(promoPrice || 200)}
                    </div>
                  }
                />
              )}
            {!isBookOnSale &&
              !isNotAvailableInLib &&
              !owned &&
              !isAvailableInLib &&
              !isFreeBook &&
              !isPodcastParent &&
              !isHidden &&
              url &&
              price && (
                <Button
                  mode="secondary"
                  size="small"
                  onClick={(
                    e: React.MouseEvent<HTMLElement, MouseEvent>
                  ): void => {
                    e.stopPropagation();
                    e.preventDefault();
                    trackerSpotlightClickButton(eventData, true);
                    window.location.href = isAnon
                      ? `${url}#buy_now_noreg`
                      : `${url}#buy_now_reg`;
                  }}
                  text={
                    <>
                      {formatPrice(price)}{" "}
                      <span className={styles.litresRuble}>₽</span>
                    </>
                  }
                />
              )}
          </div>
        )}
      </SmartLink>
    </li>
  );
};
